import tools from 'altrac-conversions';
import moment from 'moment';

const TIMEBLOCK_MAX = 10080;

function expectedsolenoidState(s, valveNumber) {
  let solenoidStateShouldBe = 0;
  let valveRecent = false;

  if (!s?.settings) {
    return { solenoidStateShouldBe, valveRecent };
  }

  const { valveTime, timeblocks } = s.settings;

  if (timeblocks?.[String(valveNumber)]) {
    const block = timeblocks[String(valveNumber)];
    for (const individual of block) {
      const { start, stop } = individual;
      const diff = stop - start;
      const currentMin = moment.utc().diff(moment.utc().startOf('week'), 'minutes');

      if (
        (currentMin > start && currentMin < stop) ||
        (diff < 0 && currentMin > start && currentMin > stop) ||
        (diff < 0 && currentMin < start && currentMin < stop)
      ) {
        solenoidStateShouldBe = 1;
      }

      const recentStartForward = start - currentMin;
      const recentStartBackward = start - currentMin - TIMEBLOCK_MAX;
      const recentStart = Math.abs(recentStartForward) > Math.abs(recentStartBackward)
        ? recentStartBackward
        : recentStartForward;
      const recentStopForward = stop - currentMin;
      const recentStopBackward = stop - currentMin - TIMEBLOCK_MAX;
      const recentStop = Math.abs(recentStopForward) > Math.abs(recentStopBackward)
        ? recentStopBackward
        : recentStopForward;

      if (Math.abs(recentStart) <= 5 || Math.abs(recentStop) <= 5) {
        valveRecent = true;
      }
    }
    return { solenoidStateShouldBe, valveRecent };
  }

  const now = Date.now();

  if (valveTime) {
    let valveTimes = tools.nextValveTimeToEpochMillis(valveTime, valveNumber);
    if (valveTimes[0] === 0 && valveTimes[1] === 0) {
      valveTimes = tools.lastValveTimeToEpochMillis(valveTime, valveNumber);
    }

    if (now < valveTimes[0] || (valveTimes[0] === 0 && valveTimes[1] === 0)) {
      solenoidStateShouldBe = 0;
    } else if (now > valveTimes[0] && now < valveTimes[1]) {
      solenoidStateShouldBe = 1;
    } else {
      solenoidStateShouldBe = 0;
    }
    valveRecent = now - valveTimes[0] < 300000; // 5 minutes
  }

  return { solenoidStateShouldBe, valveRecent };
}

export function statusValve(status, device) {
  const reading0 = device.reading0 || device.readingLast || {};

  const interfaceData = device.interface_versioned?.data ?? {};
  const other = interfaceData.other ?? {};
  const page = other.page ?? 'valve';
  const valveNumberInterface = other.valveNumber ?? 1;

  if (page !== 'valve') return;

  let valveNumber = Number(valveNumberInterface) > 0 ? Number(valveNumberInterface) : 1;

  const solenoidState = tools.numberToBinary(reading0['131'], valveNumber - 1) ? 1 : 0;
  const pressureState = tools.numberToBinary(reading0['134'], valveNumber - 1) ? 1 : 0;
  const solenoidResult = valveNumber === 1 ? reading0['154'] : reading0['157'];

  const { solenoidStateShouldBe, valveRecent } = expectedsolenoidState(
    device.application_settings,
    valveNumber,
  );

  let valveRank = '50';
  let valveId = 'none';
  let valveCode = 'normal';
  let valveShortMessage = 'No data';
  let valveMessage = 'No data';
  const deviceName = device?.physical?.deviceNumber ?? 'no name';

  if (solenoidStateShouldBe === 1) {
    if (solenoidResult === 0) {
      if (solenoidState === 1 && pressureState === 1) {
        valveShortMessage = `Valve ${deviceName} on`;
        valveMessage = `Valve ${deviceName} is on`;
        valveRank = 10;
        valveId = 'valveOnAndShouldBeOn';
        valveCode = 'success';
        status.stateIndicator = true;
      } else if (pressureState === 0 && solenoidState === 1) {
        valveShortMessage = `Valve ${deviceName} on`;
        valveMessage = `Valve ${deviceName} is on`;
        valveRank = 2;
        valveId = valveRecent ? 'valvePressureOffSolenoidOnScheduledRecent' : 'valvePressureOffSolenoidOnScheduled';
        valveCode = valveRecent ? 'info' : 'danger';
        status.stateIndicator = true;
        status.deviceHeadlineWarning.enabled = true;
        status.deviceHeadlineWarning.text.push('No pressure');
      }
    } else if (pressureState === 1) {
      valveShortMessage = `Valve ${deviceName} on / solenoid error`;
      valveMessage = `Valve ${deviceName} is on / solenoid error`;
      valveRank = 10;
      valveId = 'valvePressureOnSolenoidErrorScheduled';
      valveCode = 'success';
      status.stateIndicator = true;
    } else {
      valveShortMessage = `Valve ${deviceName} off / solenoid error`;
      valveMessage = `Valve ${deviceName} is off / solenoid error`;
      valveRank = 2;
      valveId = 'valvePressureOffSolenoidErrorScheduled';
      valveCode = 'danger';
      status.stateIndicator = true;
    }
  } else if (valveRecent && pressureState === 1) {
    valveShortMessage = `Valve ${deviceName} on`;
    valveMessage = `Valve ${deviceName} should be on`;
    valveRank = 2;
    valveId = 'valveOnAndShouldBeOffScheduled';
    valveCode = 'info';
    status.deviceHeadlineWarning.enabled = true;
    status.deviceHeadlineWarning.text.push('Valve not open');
  } else if (pressureState === 1 && solenoidState === 0) {
    valveShortMessage = `Valve ${deviceName} on`;
    valveMessage = `Valve ${deviceName} is on`;
    valveRank = 10;
    valveId = 'valvePressureOnSolenoidOffNotScheduled';
    valveCode = 'success';
    status.manualIndicator = true;
  } else if (pressureState === 1 && solenoidState === 1) {
    valveRank = 2;
    valveCode = 'success';
    valveId = 'valvePressureOnSolenoidOnNotScheduled';
    status.deviceHeadlineDanger.enabled = true;
    status.deviceHeadlineDanger.text.push('Block should be off, valve open');
    status.manualIndicator = true;
  }

  status.other.push({
    rank: valveRank,
    id: valveId,
    code: valveCode,
    category: 'valve',
    shortMessage: valveShortMessage,
    message: valveMessage,
  });

  if (reading0['138'] !== undefined) {
    switch (reading0['138']) {
      case 2:
        status.other.push({
          rank: 1,
          id: 'solenoidDriverNotConnected',
          code: 'deviceProblem',
          category: 'sensor',
          shortMessage: `Valve ${deviceName} solenoid Driver Not Connected`,
          message: `Valve ${deviceName} solenoid driver is not connected to the device`,
        });
        status.deviceHeadlineDanger.enabled = true;
        status.deviceHeadlineDanger.text.push('Solenoid driver is not connected');
        break;
      case 3:
        status.other.push({
          rank: 100,
          id: 'solenoidDriverConnected',
          code: 'normal',
          category: 'sensor',
          shortMessage: `Valve ${deviceName} solenoid Driver Connected`,
          message: `Valve ${deviceName} solenoid driver is connected`,
        });
        break;
      default:
        break;
    }
  }

  const batteryExternalWarning = 2.2;
  const batteryExternal = tools.percentTo20V(reading0['130']);

  if (batteryExternal < batteryExternalWarning) {
    status.other.push({
      rank: 10,
      id: 'solenoidDriverLowPower',
      code: 'deviceProblem',
      category: 'sensor',
      shortMessage: `Valve ${deviceName} low SD Power`,
      message: `Valve ${deviceName} solenoid power is ${batteryExternal}V which is below the ${batteryExternalWarning}V warning level.`,
    });
    status.deviceHeadlineDanger.enabled = true;
    status.deviceHeadlineDanger.text.push('Solenoid power is below 2.3V');
  }
}
